import { ArrowRight, ExternalLink, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type { AppliedTransformation } from '../../utils/export-preview-model';

interface PreviewEncodingsViewProps {
  /** The distinct encodings this template applies, most-used first. */
  transformations: AppliedTransformation[];
  /** The vendor feed spec these encodings reproduce, when the config names one. */
  specUrl: string | null;
  /** See `PreviewFileView`'s prop of the same name: every existing caller renders this inside a
   *  `Sheet` that owns the page's one scroller, so the default stays exactly as it was. The
   *  catalog builder's "File preview" tab opts in instead of forking this view. */
  boundedHeight?: boolean;
}

/**
 * A reference for every formatting rule the chosen template applies: a worked
 * before/after pair, one plain sentence on what it does, and the columns it
 * touches.
 *
 * Built from `GET /exports/templates` config already fetched for the picker.
 * The example leads and the prose follows, because the people reading this are
 * merchandisers, not the engineers who maintain the rules: a before → after
 * reads at a glance where a paragraph about `~` segments does not. The
 * service's `description` is written for the maintainers — it names storage
 * columns and jsonb shapes — so it moves behind an info affordance alongside
 * the rule's code, and the sentence shown is a plain-language summary held in
 * the locale file, keyed on the code, falling back to the description for a
 * rule that has none yet.
 */
export function PreviewEncodingsView({
  transformations,
  specUrl,
  boundedHeight = false,
}: PreviewEncodingsViewProps) {
  const { t } = useTranslation('exports');

  return (
    // Default: no scroller and no cap — the drawer's region owns the vertical axis.
    // `boundedHeight` opts into its own bounded box for a caller with no such outer scroller.
    <div
      data-testid="export-preview-encodings"
      className={cn(
        'space-y-2 pr-1',
        boundedHeight && 'max-h-[70vh] min-h-0 flex-1 overflow-y-auto',
      )}
    >
      {specUrl && (
        <p className="text-muted-foreground text-xs">
          {t('preview.encodings.specIntro')}
          <Button
            asChild
            variant="link"
            size="sm"
            className="h-auto cursor-pointer p-0 pl-1 text-xs"
          >
            <a
              href={specUrl}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="export-preview-encodings-spec-link"
            >
              {t('preview.encodings.specLink')}
              <ExternalLink aria-hidden="true" className="ml-0.5 size-3" />
            </a>
          </Button>
        </p>
      )}

      {transformations.map((transformation) => {
        // A missing summary falls back to the service's own description rather
        // than to the key, so a rule added server-side still reads as prose.
        const summary = t(`preview.encodings.summary.${transformation.code}`, {
          defaultValue: transformation.description,
        });
        return (
          <div
            key={transformation.code}
            data-testid={`export-preview-encoding-card-${transformation.code}`}
            className="rounded-md border"
          >
            <div className="bg-selected-row flex flex-wrap items-center gap-x-2 border-b px-3 py-2">
              <span className="text-foreground text-sm font-semibold">{transformation.label}</span>
              {/* The maintainers' description and the rule's code, on demand.
                  Inline they were the first thing on the card, and neither
                  means anything to the operator the card is for. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground cursor-help"
                    aria-label={t('preview.encodings.detailsAria', { rule: transformation.label })}
                    data-testid={`export-preview-encoding-info-${transformation.code}`}
                  >
                    <Info aria-hidden="true" className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm space-y-1 text-xs leading-relaxed">
                  <p>{transformation.description}</p>
                  <p className="text-muted-foreground font-mono">{transformation.code}</p>
                </TooltipContent>
              </Tooltip>
              <Badge
                variant="outline"
                className="pointer-events-none ml-auto text-[11px] font-normal"
              >
                {t('preview.encodings.columnCount', { count: transformation.columnLabels.length })}
              </Badge>
            </div>

            <div className="space-y-2.5 px-3 py-2.5">
              {/* The worked example is the hero: before → after on one line
                  where it fits, and a blank result says so in words, since an
                  empty box reads as a rendering fault rather than a rule that
                  clears the value. */}
              <div className="flex flex-wrap items-center gap-2 font-mono text-sm">
                <code className="text-muted-foreground bg-muted rounded px-2 py-1 break-all">
                  {transformation.before || t('preview.changes.emptyValue')}
                </code>
                <ArrowRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
                <code className="text-foreground bg-success-50/60 rounded px-2 py-1 break-all">
                  {transformation.after || t('preview.changes.emptyValue')}
                </code>
              </div>

              <p className="text-foreground text-sm leading-5">{summary}</p>

              <div className="flex flex-wrap items-center gap-1">
                <span className="text-muted-foreground mr-0.5 text-xs">
                  {t('preview.encodings.appliesTo')}
                </span>
                {transformation.columnLabels.map((label, index) => (
                  // Column labels can legitimately repeat across a connector's
                  // composite fields, so the index is folded into the key.
                  <Badge
                    key={`${label}-${String(index)}`}
                    variant="outline"
                    className="pointer-events-none text-[11px] font-normal"
                  >
                    {label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
